import GeneralTracker from '../GeneralTracker';


const SPONSORED_AD_THRESHOLD = 1000;
const VISIBILITY_THRESHOLD = 0.1;
const CLICK_FOR_CLOSE_TOLERANCE = 500;
const VISIBILITY_TIME_TOLERANCE = 300;

export default class AdsInteractionTracker extends GeneralTracker {
  CAPTURE_NAME = 'ui:ads:interaction';
  EVENT_GROUP = 'ads';
  VERSION = '1';
  AD_TYPES = {
    'companion': 1,
    'embeded': 2,
    'interstitial': 3,
    'sponsored': 4,
    'static': 5,
    'video': 6,
    'popup': 7,
    'popunder': 8,
  };

  init(service) {
    super.init(service);
    this.curentAds = {};
  }

  setHandlers() {
    const service = this.getService();

    service.on('adLoaded', this.onAdLoaded);
    service.on('adShow', this.onAdShow);
    service.on('adClose', this.onAdClose);
    service.on('adClick', this.onAdClick);
    service.on('adPaused', this.onAdPaused);
    service.on('adResumed', this.onAdResumed);
    service.on('adRemove', this.onAdRemove);
    service.on('adHover', this.onAdHover);
    service.on('adRefresh', this.onAdRefresh);
    service.on('adError', this.onAdError);
  }

  getUniqueAdId = (adType, adKey) => {
    const adTypeNumber = this.AD_TYPES[adKey];

    if (adTypeNumber === undefined) {
      throw new Error(`Unknown ad type "${adType}"`);
    }

    const hash = this.service.generateUniqueID(adTypeNumber);
    const id = `${adType}:${hash}`;

    return id;
  };

  getAdType = (adType) => adType.split('.')[0];

  onAdLoaded = (payload) => {
    const { adType, container, newWindow } = payload;
    const adKey = this.getAdType(adType);

    this.curentAds[adType] = {
      start: Date.now(),
      details: payload,
      id: this.getUniqueAdId(adType, adKey),
    };

    if (adKey !== adType) {
      const generalAdType = `${adKey}.*`;

      if (!(generalAdType in this.curentAds)) {
        this.curentAds[generalAdType] = {
          start: Date.now(),
          id: this.getUniqueAdId(adType, adKey),
          ads: {},
        };
      }

      this.curentAds[generalAdType].ads[adType] = this.curentAds[adType];
    }

    const adShown = () => {
      const service = this.getService();
      service.emit('adShow', payload);

      this.curentAds[adType].shown = true;
    };

    if (container !== false) {
      if (!container) {
        this.logger.error(`Ad container is missing for ad of type ${adType}`);

        return;
      }

      const timeouts = {};
      const observer = new IntersectionObserver((entries, currentObserver) => {
        entries.forEach((entry) => {
          const { target } = entry;

          if (entry.isIntersecting) {
            timeouts[target.id] = setTimeout(() => {
              adShown(payload);

              // we consider there is only one target for the observer
              // if multiple targets. use `unobserve` instead, and
              // handle observer disconnect elsewhere
              currentObserver.disconnect();
            }, VISIBILITY_TIME_TOLERANCE);
          } else {
            clearTimeout(timeouts[target.id]);
          }
        });
      }, {
        root: null,
        rootMargin: '0px',
        threshold: VISIBILITY_THRESHOLD,
      });
      observer.observe(container);
    } else {
      this.curentAds[adType].noContainer = true;
      if (newWindow === true) {
        adShown(payload);
      } else {
        // this is to handle the other no container ad: popunder
        // when the interaction is more clearer we can fix this
        adShown(payload);
      }
    }
  };

  onAdShow = (payload) => {
    const trackPayload = {
      action: 'show',
      details: payload,
    };

    const { adType } = payload;
    const ad = this.curentAds[adType];

    if (!ad) {
      this.logger.error(`Unregistered ad of type ${adType} displayed`);

      return;
    }

    this.track(trackPayload);
  };

  onAdClose = (payload) => {
    const trackPayload = {
      action: 'close',
      details: payload,
    };

    const { adType } = payload;

    if (this.curentAds?.[adType] !== undefined) {
      this.curentAds[adType].closed = true;

      this.track(trackPayload);
    } else {
      this.logger.error(`Unregistered ad of type ${adType} closed`);
    }
  };

  adError = (payload) => {
    const { adType } = payload;

    if (!(adType in this.curentAds)) {
      const adKey = this.getAdType(adType);
      this.curentAds[adType] = {
        start: Date.now(),
        id: this.getUniqueAdId(adType, adKey),
      };
    }

    const trackPayload = {
      action: 'error',
      details: payload,
    };

    this.track(trackPayload);
  };

  onAdRefresh = (payload) => {
    const trackPayload = {
      action: 'refresh',
      details: payload,
    };

    const { adType } = payload;
    this.curentAds[adType] = {
      start: Date.now(),
      details: payload,
    };

    this.track(trackPayload);
  };

  onAdPaused = (payload) => {
    const trackPayload = {
      action: 'pause',
      details: payload,
    };

    const { adType } = payload;

    if (this.curentAds?.[adType] !== undefined) {
      this.curentAds[adType].pause = Date.now();
      this.track(trackPayload);
    } else {
      this.logger.error(`Unregistered ad of type ${adType} paused`);
    }
  };

  onAdResumed = (payload) => {
    const trackPayload = {
      action: 'resume',
      details: payload,
    };

    const { adType } = payload;

    if (this.curentAds?.[adType] !== undefined) {
      const { pause } = this.curentAds[adType];
      const pauseDuration = Date.now() - pause;

      trackPayload.pauseDuration = pauseDuration;
      this.track(trackPayload);
    } else {
      this.logger.error(`Unregistered ad of type ${adType} resumed`);
    }
  };

  onAdClick = (payload) => {
    const { adType } = payload;

    const trackPayload = {
      action: 'click',
      details: {
        timestamp: Date.now(),
        ...payload,
      },
    };

    if (this.curentAds?.[adType]?.closed === true) {
      return;
    }

    setTimeout(() => {
      if (this.curentAds?.[adType]?.closed !== true) {
        this.track(trackPayload);
      }
    }, CLICK_FOR_CLOSE_TOLERANCE);
  };

  onAdRemove = (payload) => {
    const trackPayload = {
      action: 'remove',
      details: payload,
    };

    this.track(trackPayload);
  };

  onAdHover = (payload) => {
    const trackPayload = { action: 'hover' };
    const { adType, interaction, ...details } = payload;

    if (!(this?.curentAds?.[adType])) {
      this.logger.error(`Unregistered ad of type ${adType} hovered`);

      return;
    }

    const ad = this.curentAds[adType];
    const { timeout, mouseEnter, entered } = ad;

    if (interaction === 'enter') {
      trackPayload.details = { moment: { enter: Date.now() } };
      ad.mouseEnter = Date.now();
    } else if (interaction === 'leave') {
      trackPayload.details = {
        moment: {
          leave: Date.now(),
          ...mouseEnter && { duration: Date.now() - mouseEnter },
        },
      };
    }

    trackPayload.details = {
      adType,
      ...trackPayload.details,
      ...details,
    };

    if (interaction === 'enter') {
      clearTimeout(timeout);
      delete ad.entered;

      ad.timeout = setTimeout(() => {
        ad.entered = true;

        this.track(trackPayload);
      }, SPONSORED_AD_THRESHOLD);
    } else if (interaction === 'leave') {
      if (entered) {
        this.track(trackPayload);
      }

      clearTimeout(timeout);
      delete ad.entered;
      delete ad.timeout;
    }
  };

  parsePayload = (payload) => {
    const { action, details: payloadDetails, ...payloadOther } = payload;
    const { adType, container, ...payloadDetailsOther } = payloadDetails;

    if (this.curentAds?.[adType] === undefined) {
      this.logger.error(`Ad of type ${adType} is not registered`);

      return false;
    }

    const { id: adTrackingId, ads: subAds, start, details: adDetails } = this?.curentAds?.[adType];

    if (container && 'container' in adDetails) {
      delete adDetails.container;
    }

    const trackPayload = {
      action: `${action}Ad`,
      labels: {
        adTrackingId,
        ...payloadOther,
        ...subAds && subAds,
        ...payloadDetailsOther,
        ...adDetails,
        ...start && { timeSinceLoaded: Date.now() - start },
      },
    };

    return trackPayload;
  };

  track = (payload) => {
    const trackPayload = this.parsePayload(payload);

    if (trackPayload === false) {
      this.logger.error('Ad payload parsing failed');

      return;
    }

    super.track(trackPayload);
  };
}
